<template>
  <SoAlert
    v-if="productsQuery.isError"
    title="Could not load energy products"
    data-testid="energy-product-list-error"
  />

  <SoDataTable
    v-else
    ref="energy-product-list-table"
    :headers="headers"
    :items="productList"
    :loading="productsQuery.isFetching"
    :default-items-per-page="25"
    data-testid="energy-product-list-table"
  >
    <template #tableActions>
      <div class="ml-5 mr-auto d-flex gap-3">
        <SoToggle
          v-model="filters.showActiveOnly"
          label="Show only active products"
          data-testid="energy-product-list-active-toggle"
          class="mt-1"
        />
        <SoToggle
          v-model="filters.hideNscProducts"
          label="Hide NSC products"
          data-testid="energy-product-list-nsc-toggle"
          class="mt-1"
        />
      </div>
      <template v-if="hasUnsavedChanges">
        <SoButton title="Cancel" class="mr-3" large @click="resetChanges" />
        <SoButton
          title="Review & Save"
          type="success"
          large
          data-testid="energy-product-review-and-save-btn"
          @click="openSaveAndReviewModal = true"
        />
      </template>
    </template>

    <template #item.isActiveForEnrolment="{ internalItem }">
      <SoToggle
        v-model="productList[internalItem.index].isActiveForEnrolment"
        :data-testid="`active-for-enrolment-${internalItem.value}`"
        name="product-is_active-for-enrolment"
      />
    </template>

    <template #item.isActiveForRenewal="{ internalItem }">
      <SoToggle
        v-model="productList[internalItem.index].isActiveForRenewal"
        :data-testid="`active-for-renewal-${internalItem.value}`"
        name="product-is_active-for-renewal"
      />
    </template>

    <template #item.isRecommendedForEnrolment="{ internalItem }">
      <SoToggle
        v-model="productList[internalItem.index].isRecommendedForEnrolment"
        :data-testid="`recommended-for-enrolment-${internalItem.value}`"
        name="product-is_recommended-for-enrolment"
      />
    </template>

    <template #item.isRecommendedForRenewal="{ internalItem }">
      <SoToggle
        v-model="productList[internalItem.index].isRecommendedForRenewal"
        :data-testid="`recommended-for-renewal-${internalItem.value}`"
        name="product-is_recommended-for-renewal"
      />
    </template>

    <template #item.requiresSmartCharging="{ internalItem }">
      <SoToggle
        v-model="productList[internalItem.index].requiresSmartCharging"
        :disabled="!internalItem.raw.displayName?.includes(' EV ')"
        :data-testid="`requires-smart-charging-${internalItem.value}`"
        name="product-requires-smart-charging"
      />
    </template>
  </SoDataTable>

  <AdminProductsUpdateModal
    v-model="openSaveAndReviewModal"
    :changes="changes"
    @success="productsQuery.refetch()"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { ProductService } from "@/services/admin/ProductService"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import { cloneDeep, isEmpty, pick } from "lodash-es"
  import { IProduct } from "@/models/admin/product/product.types"
  import { getObjectDiff } from "@/helpers/objectHelpers"
  import AdminProductsUpdateModal, {
    EnergyProductListForm,
  } from "@/views/admin/energyProducts/modals/AdminProductsUpdateModal.vue"

  const tableHeaders: SoDataTableHeader[] = [
    {
      key: "id",
      title: "ID",
    },
    {
      key: "displayName",
      title: "Display Name",
    },
    {
      key: "code",
      title: "Code",
    },
    {
      key: "duration",
      title: "Duration",
      align: "end",
      fallbackValue: "N/A",
    },
    {
      key: "isActiveForEnrolment",
      title: "Active For Enrolment",
    },
    {
      key: "isActiveForRenewal",
      title: "Active For Renewal",
    },
    {
      key: "isRecommendedForEnrolment",
      title: "Recommend For Enrolment",
    },
    {
      key: "isRecommendedForRenewal",
      title: "Recommend For Renewal",
    },
    {
      key: "requiresSmartCharging",
      title: "Requires Smart Charging",
    },
  ]

  export default defineComponent({
    components: {
      SoAlert,
      SoDataTable,
      SoButton,
      SoToggle,
      AdminProductsUpdateModal,
    },

    data() {
      return {
        productsQuery: ProductService.getProductList(),
        headers: tableHeaders,
        openSaveAndReviewModal: false,
        filters: {
          showActiveOnly: true,
          hideNscProducts: true,
        },
        productList: [] as IProduct[],
      }
    },

    computed: {
      products() {
        const { hideNscProducts, showActiveOnly } = this.filters
        return this.productsQuery.data.filter((p) => {
          const isProductActive = p.isActiveForEnrolment || p.isActiveForRenewal

          if (hideNscProducts && p.isNscProduct) return false
          if (showActiveOnly && !isProductActive) return false

          return true
        })
      },
      hasUnsavedChanges() {
        return !isEmpty(this.changes)
      },
      changes() {
        return this.productList.reduce((products, product, key) => {
          const originalProduct = this.products[key]
          const updatedProperties = getObjectDiff(product, originalProduct)

          if (updatedProperties.length && product.id) {
            products[product.id] = {
              original: originalProduct,
              update: pick(product, updatedProperties),
            }
          }
          return products
        }, {} as EnergyProductListForm)
      },
    },

    watch: {
      products(products) {
        this.productList = cloneDeep(products)
      },
    },

    created() {
      this.productList = cloneDeep(this.products)
    },

    methods: {
      resetChanges() {
        this.productList = cloneDeep(this.products)
      },
    },
  })
</script>
